import { h, FunctionComponent } from 'preact';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  CurrencyInputProps,
  InputGroupProps,
  InputProps,
  MaskedInputProps,
} from '../types/forms';
import CurrencyInput from './CurrencyInput';
import InfoTooltip from './InfoTooltip';
import Input from './Input';
import Label from './Label';
import MaskedInput from './MaskedInput';
import { Page } from '../enums/api';

/**
 Usage: A <input> with css class, label
 */
const InputGroup: FunctionComponent<InputGroupProps> = props => {
  const {
    input,
    id,
    page,
    state,
    reduxActions,
    groupType,
    centered = false,
    className = '',
    extras,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const onChangeHandler = () => {
    return (value: string) => {
      dispatch(
        reduxActions.updateInputValue({
          name: id,
          value: value.replace(
            /[\x00-\x1F\x7F-\x9F\u200B-\u200F\u2028\u2029\uFEFF]/g,
            '',
          ),
        }),
      );
    };
  };

  const inputProps = {
    ...input,
    id,
    centered,
    type: groupType || 'text',
    className: `form-control ${className}`,
    onChangeHandler: onChangeHandler(),
    value: state.value,
    isDisabled: state.isDisabled,
    currencyConfig: extras?.includes('currencyConfig')
      ? t('common:defaultCurrencyConfig', { returnObjects: true })
      : undefined,
  };

  // We have a separate set of inputs on start page because
  // For onboard spend we remove some tooltips
  // However other pages with tooltips does not had any onboard spend changes
  // So no need to look for the inputs inside 'regular-application' key
  const inputPrefix = page == Page.START ? 'regular-application.' : '';

  return state.isVisible !== false ? (
    <div
      id={`group-${id}`}
      className={`form-group ${
        state.isValid !== null && state.isValid === false ? 'has-error' : ''
      }`}
    >
      <Label
        label={input.label}
        id={id}
        isValid={state.isValid}
        errorMessage={state.errorMessage}
        showRequiredIcon={input.showRequiredIcon}
      />
      {input.tooltip && (
        <InfoTooltip id={`${id}-tooltip`}>
          <Trans t={t} i18nKey={`${page}:${inputPrefix}inputs.${id}.tooltip`} />
        </InfoTooltip>
      )}
      {(inputProps.currencyConfig && (
        <CurrencyInput {...(inputProps as CurrencyInputProps)} />
      )) ||
        (input.mask && (
          <MaskedInput {...(inputProps as unknown as MaskedInputProps)} />
        )) || <Input {...(inputProps as InputProps)} />}
    </div>
  ) : null;
};

export default InputGroup;
